import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, PhoneCall, AlertTriangle, LogIn, LogOut, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDay, fmtTime, formatErr } from "../../lib/api";
import { Loading, StatusPill } from "../../components/Shared";

const CHECKLIST = ["Wellbeing conversation", "Medication stock check", "Home safety check", "Kitchen & essentials", "Companionship time"];
const CATEGORIES = ["fall", "illness_health_concern", "hospitalisation", "missed_appointment", "caregiver_issue", "safety_concern", "service_provider_problem", "other"];
const SEVERITIES = ["routine", "attention_required", "urgent", "emergency"];

export default function CMVisit() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [obs, setObs] = useState("");
  const [note, setNote] = useState("");
  const [vitals, setVitals] = useState({ bp: "", pulse: "", spo2: "" });
  const [followup, setFollowup] = useState("");
  const [followups, setFollowups] = useState([]);
  const [showIncident, setShowIncident] = useState(false);
  const [incident, setIncident] = useState({ category: "other", severity: "routine", description: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/cm/visits/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, [id]);
  if (!d) return <Loading />;
  const { visit, parent, medical, emergency_contacts } = d;

  const doCheckIn = async () => {
    setBusy(true);
    try { await api.post(`/cm/visits/${id}/check-in`); toast.success("Checked in. Family notified."); load(); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  const doCheckOut = async () => {
    setBusy(true);
    try {
      await api.post(`/cm/visits/${id}/check-out`, {
        tasks_completed: tasks, observations: obs, note,
        vitals: Object.fromEntries(Object.entries(vitals).filter(([, v]) => v)),
        followups,
      });
      toast.success("Visit completed. Report sent to the family.");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  const doIncident = async () => {
    try {
      await api.post("/cm/incidents", { ...incident, parent_id: parent.id });
      toast.success(incident.severity === "emergency" ? "Emergency case opened — operations alerted immediately." : "Incident reported.");
      setShowIncident(false);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="cm-visit-detail">
      <Link to="/app/cm" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-900" data-testid="cm-back"><ArrowLeft className="h-4 w-4" /> Back to Today</Link>

      <div className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold text-stone-900">{parent?.name}</h1>
            <p className="text-sm text-stone-500">{parent?.age} yrs · {parent?.preferred_language}</p>
            <p className="mt-1 flex items-start gap-1 text-xs text-stone-400"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {parent?.address}</p>
          </div>
          <StatusPill status={visit.status} />
        </div>
        <p className="mt-3 rounded-xl bg-stone-50 p-3 text-sm text-stone-700"><strong>{visit.purpose}</strong> · {fmtDay(visit.scheduled_at)}, {fmtTime(visit.scheduled_at)}</p>
        {medical && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="cm-medical-brief">
            <strong>Care brief:</strong> {(medical.conditions || []).join(", ") || "—"} · Allergies: {(medical.allergies || []).join(", ") || "none recorded"}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {(emergency_contacts || []).slice(0, 2).map((c) => (
            c.phone ? (
              <a key={c.id} href={`tel:${c.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600" data-testid={`cm-ec-${c.id}`}>
                <PhoneCall className="h-3.5 w-3.5" /> {c.name}
              </a>
            ) : (
              <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-stone-200 px-3 py-1.5 text-xs text-stone-400" data-testid={`cm-ec-${c.id}`}>
                <PhoneCall className="h-3.5 w-3.5" /> {c.name} — number on file soon
              </span>
            )
          ))}
        </div>
      </div>

      {visit.status === "scheduled" && (
        <button onClick={doCheckIn} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-900 py-4 font-heading text-lg font-bold text-white shadow-lg active:scale-[0.98]" data-testid="cm-check-in-btn">
          <LogIn className="h-5 w-5" /> {busy ? "Checking in…" : "Start Visit — Check In"}
        </button>
      )}

      {visit.status === "in_progress" && (
        <div className="space-y-5" data-testid="cm-visit-form">
          <div className="rounded-3xl border border-stone-200 bg-white p-5">
            <p className="p1-label">Visit checklist</p>
            <div className="space-y-2">
              {CHECKLIST.map((c) => (
                <label key={c} className="flex items-center gap-3 rounded-xl border border-stone-100 p-3 text-sm font-medium text-stone-700">
                  <input type="checkbox" className="h-5 w-5 accent-emerald-800" checked={tasks.includes(c)}
                    onChange={(e) => setTasks(e.target.checked ? [...tasks, c] : tasks.filter((t) => t !== c))}
                    data-testid={`cm-task-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} /> {c}
                </label>
              ))}
            </div>
            <p className="p1-label mt-4">Observations (shared with family)</p>
            <textarea className="p1-input" rows={3} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="How is your parent today? Anything the family should know?" data-testid="cm-observations" />
            <p className="p1-label mt-4">Vitals (only if you are authorised to record them)</p>
            <div className="grid grid-cols-3 gap-2">
              <input className="p1-input" placeholder="BP" value={vitals.bp} onChange={(e) => setVitals({ ...vitals, bp: e.target.value })} data-testid="cm-vital-bp" />
              <input className="p1-input" placeholder="Pulse" value={vitals.pulse} onChange={(e) => setVitals({ ...vitals, pulse: e.target.value })} data-testid="cm-vital-pulse" />
              <input className="p1-input" placeholder="SpO2" value={vitals.spo2} onChange={(e) => setVitals({ ...vitals, spo2: e.target.value })} data-testid="cm-vital-spo2" />
            </div>
            <p className="p1-label mt-4">Private note for family</p>
            <textarea className="p1-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="cm-note" />
            <p className="p1-label mt-4">Follow-up actions</p>
            <div className="flex gap-2">
              <input className="p1-input" value={followup} onChange={(e) => setFollowup(e.target.value)} placeholder="e.g. Recheck medicines next week" data-testid="cm-followup-input" />
              <button type="button" onClick={() => { if (followup.trim()) { setFollowups([...followups, followup.trim()]); setFollowup(""); } }} className="rounded-xl bg-stone-800 px-4 text-white" data-testid="cm-followup-add"><Plus className="h-4 w-4" /></button>
            </div>
            {followups.length > 0 && <ul className="mt-2 space-y-1">{followups.map((f, i) => <li key={i} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-1.5 text-xs">{f}<button onClick={() => setFollowups(followups.filter((_, j) => j !== i))}><X className="h-3.5 w-3.5 text-stone-400" /></button></li>)}</ul>}
          </div>
          <button onClick={doCheckOut} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-900 py-4 font-heading text-lg font-bold text-white shadow-lg active:scale-[0.98]" data-testid="cm-check-out-btn">
            <LogOut className="h-5 w-5" /> {busy ? "Submitting…" : "Complete Visit — Check Out & Send Report"}
          </button>
        </div>
      )}

      {visit.status === "completed" && (
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="cm-visit-done">
          <p className="font-heading text-lg font-bold text-emerald-900">Visit completed</p>
          <p className="text-sm text-emerald-800">{fmtTime(visit.check_in_at)} → {fmtTime(visit.check_out_at)} · Report delivered to family.</p>
        </div>
      )}

      <button onClick={() => setShowIncident(!showIncident)} className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-red-200 bg-white py-3.5 font-semibold text-red-700" data-testid="cm-report-incident-btn">
        <AlertTriangle className="h-5 w-5" /> Report a Concern / Incident
      </button>
      {showIncident && (
        <div className="rounded-3xl border-2 border-red-200 bg-white p-5" data-testid="cm-incident-form">
          <p className="p1-label">Category</p>
          <select className="p1-input" value={incident.category} onChange={(e) => setIncident({ ...incident, category: e.target.value })} data-testid="cm-incident-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
          <p className="p1-label mt-3">Severity</p>
          <div className="grid grid-cols-2 gap-2">
            {SEVERITIES.map((s) => (
              <button key={s} onClick={() => setIncident({ ...incident, severity: s })}
                className={`rounded-xl border-2 p-2.5 text-xs font-bold uppercase tracking-wide ${incident.severity === s ? (s === "emergency" ? "border-red-600 bg-red-50 text-red-700" : "border-emerald-800 bg-emerald-50 text-emerald-900") : "border-stone-200 text-stone-500"}`}
                data-testid={`cm-severity-${s}`}>{s.replace(/_/g, " ")}</button>
            ))}
          </div>
          <p className="p1-label mt-3">What happened?</p>
          <textarea className="p1-input" rows={3} value={incident.description} onChange={(e) => setIncident({ ...incident, description: e.target.value })} data-testid="cm-incident-description" />
          {incident.severity === "emergency" && <p className="mt-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">Emergency incidents open a case in the Operations Console immediately.</p>}
          <button onClick={doIncident} className="mt-3 w-full rounded-2xl bg-red-600 py-3 font-bold text-white" data-testid="cm-incident-submit">Submit Incident Report</button>
        </div>
      )}
    </div>
  );
}
